import { useState } from "react";
import { HiOutlineClock, HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineEye } from "react-icons/hi";
import Card from "../shared/Card";
import Button from "../shared/Button";

const TEXT_PAGE_SIZE = 10;

const SurveyAnalytics = ({ analytics, survey }) => {
    const { overview, questionAnalytics, timeDistribution } = analytics;
    const [textPage, setTextPage] = useState({});

    const renderQuestionAnalytics = (question) => {
        if (["single_choice", "multiple_choice", "dropdown"].includes(question.questionType)) {
            return (
                <div className="space-y-3">
                    {question.optionDistribution.map((option, index) => (
                        <div key={index}>
                            <div className="flex justify-between text-body-md mb-1">
                                <span className="text-ink">{option.text}</span>
                                <span className="text-muted">
                                    {option.count} ({option.percentage}%)
                                </span>
                            </div>
                            <div className="w-full bg-surface-strong rounded-full h-2">
                                <div
                                    className="h-2 rounded-full bg-ink transition-all"
                                    style={{ width: `${option.percentage}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (question.questionType === "rating_scale") {
            if (question.totalResponses === 0) {
                return <p className="text-body-md text-muted">No responses yet</p>;
            }
            return (
                <div>
                    <div className="text-title-md font-medium mb-4 text-ink">
                        Average: {question.averageRating ?? "—"}
                    </div>
                    <div className="space-y-2">
                        {Object.entries(question.ratingDistribution).map(([rating, count]) => (
                            <div key={rating} className="flex items-center gap-3">
                                <span className="w-8 font-medium text-ink">{rating}</span>
                                <div className="flex-1 bg-surface-strong rounded-full h-2">
                                    <div
                                        className="h-2 rounded-full bg-ink transition-all"
                                        style={{
                                            width: `${question.totalResponses > 0 ? (count / question.totalResponses) * 100 : 0}%`,
                                        }}
                                    />
                                </div>
                                <span className="w-12 text-body-md text-right text-muted">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (question.questionType === "nps") {
            const b = question.npsBreakdown || { promoters: 0, passives: 0, detractors: 0, total: 0, npsScore: null };
            const pct = (count) => (b.total > 0 ? Math.round((count / b.total) * 100) : 0);
            return (
                <div className="space-y-4">
                    <div className="flex items-baseline gap-3">
                        <span className="text-display-md font-medium text-ink">
                            {b.npsScore == null ? "—" : b.npsScore}
                        </span>
                        <span className="text-body-md text-muted">NPS score (based on {b.total} responses)</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <Card variant="surface-soft" className="text-center !p-4">
                            <div className="text-title-md font-medium text-signature-coral">{b.detractors}</div>
                            <div className="text-body-md text-muted">Detractors (0–6)</div>
                            <div className="text-body-md text-muted">{pct(b.detractors)}%</div>
                        </Card>
                        <Card variant="surface-soft" className="text-center !p-4">
                            <div className="text-title-md font-medium text-ink">{b.passives}</div>
                            <div className="text-body-md text-muted">Passives (7–8)</div>
                            <div className="text-body-md text-muted">{pct(b.passives)}%</div>
                        </Card>
                        <Card variant="surface-soft" className="text-center !p-4">
                            <div className="text-title-md font-medium text-signature-forest">{b.promoters}</div>
                            <div className="text-body-md text-muted">Promoters (9–10)</div>
                            <div className="text-body-md text-muted">{pct(b.promoters)}%</div>
                        </Card>
                    </div>
                </div>
            );
        }

        if (question.questionType === "yes_no") {
            return (
                <div className="grid grid-cols-2 gap-4">
                    <Card variant="surface-soft" className="text-center !p-4">
                        <div className="text-display-md font-medium mb-1 text-signature-forest">
                            {question.yesCount}
                        </div>
                        <div className="text-body-md text-muted">Yes ({question.yesPercentage}%)</div>
                    </Card>
                    <Card variant="surface-soft" className="text-center !p-4">
                        <div className="text-display-md font-medium mb-1 text-signature-coral">
                            {question.noCount}
                        </div>
                        <div className="text-body-md text-muted">No ({question.noPercentage}%)</div>
                    </Card>
                </div>
            );
        }

        if (["text_short", "text_long"].includes(question.questionType)) {
            const items = question.responses || [];
            if (items.length === 0) {
                return <p className="text-body-md text-muted">No text responses yet</p>;
            }
            const page = textPage[question.questionId] || 1;
            const start = (page - 1) * TEXT_PAGE_SIZE;
            const visible = items.slice(start, start + TEXT_PAGE_SIZE);
            const totalPages = Math.ceil(items.length / TEXT_PAGE_SIZE);
            return (
                <div className="space-y-3">
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {visible.map((response, index) => (
                            <div key={index} className="p-3 rounded-md border border-hairline bg-surface-soft">
                                <p className="text-body-md text-ink whitespace-pre-wrap break-words">{response.answer}</p>
                                <p className="text-[12px] mt-1 text-muted">
                                    {new Date(response.answeredAt).toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between">
                            <span className="text-body-md text-muted">
                                Page {page} of {totalPages} · {items.length} total
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page <= 1}
                                    onClick={() =>
                                        setTextPage({ ...textPage, [question.questionId]: page - 1 })
                                    }
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page >= totalPages}
                                    onClick={() =>
                                        setTextPage({ ...textPage, [question.questionId]: page + 1 })
                                    }
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return <p className="text-body-md text-muted">{question.totalResponses} responses</p>;
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card variant="surface-soft" className="!p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <HiOutlineEye className="text-2xl text-ink" />
                        <span className="text-body-md font-medium text-muted">Total Visits</span>
                    </div>
                    <div className="text-display-md font-medium text-ink">{overview.totalVisits}</div>
                </Card>
                <Card variant="surface-soft" className="!p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <HiOutlineCheckCircle className="text-2xl text-signature-forest" />
                        <span className="text-body-md font-medium text-muted">Completed</span>
                    </div>
                    <div className="text-display-md font-medium text-ink">{overview.totalCompleted}</div>
                </Card>
                <Card variant="surface-soft" className="!p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <HiOutlineXCircle className="text-2xl text-signature-coral" />
                        <span className="text-body-md font-medium text-muted">Abandoned</span>
                    </div>
                    <div className="text-display-md font-medium text-ink">{overview.totalAbandoned}</div>
                </Card>
                <Card variant="cream-callout" className="!p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <HiOutlineClock className="text-2xl text-ink" />
                        <span className="text-body-md font-medium text-ink">Avg. Time</span>
                    </div>
                    <div className="text-display-md font-medium text-ink">
                        {overview.averageCompletionTimeFormatted}
                    </div>
                </Card>
            </div>

            <Card variant="default">
                <h3 className="text-title-sm text-ink mb-4">Completion Rate</h3>
                <div className="flex items-center gap-4">
                    <div className="flex-1">
                        <div className="w-full bg-surface-strong rounded-full h-4">
                            <div
                                className="h-4 rounded-full bg-ink transition-all"
                                style={{ width: overview.completionRate }}
                            />
                        </div>
                    </div>
                    <div className="text-title-md font-medium text-ink">{overview.completionRate}</div>
                </div>
            </Card>

            <Card variant="default">
                <h3 className="text-title-sm text-ink mb-4">Completion Time Distribution</h3>
                <div className="space-y-3">
                    {Object.entries(timeDistribution).map(([range, count]) => {
                        const labels = {
                            under1Min: "Under 1 minute",
                            "1to3Min": "1-3 minutes",
                            "3to5Min": "3-5 minutes",
                            "5to10Min": "5-10 minutes",
                            over10Min: "Over 10 minutes",
                        };
                        const total = Object.values(timeDistribution).reduce((s, v) => s + v, 0);
                        const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                        return (
                            <div key={range}>
                                <div className="flex justify-between text-body-md mb-1">
                                    <span className="text-ink">{labels[range]}</span>
                                    <span className="text-muted">
                                        {count} ({percentage}%)
                                    </span>
                                </div>
                                <div className="w-full bg-surface-strong rounded-full h-2">
                                    <div
                                        className="h-2 rounded-full bg-ink transition-all"
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>

            <div>
                <h3 className="text-title-md text-ink mb-4">Question Analytics</h3>
                <div className="space-y-4">
                    {questionAnalytics.map((question, index) => (
                        <Card key={index} variant="default">
                            <div className="mb-4">
                                <h4 className="text-title-sm text-ink mb-2">
                                    {index + 1}. {question.questionText}
                                </h4>
                                <div className="flex gap-4 text-body-md text-muted flex-wrap">
                                    <span>Type: {question.questionType.replace("_", " ")}</span>
                                    <span>•</span>
                                    <span>{question.totalResponses} responses</span>
                                    <span>•</span>
                                    <span>Response rate: {question.responseRate}%</span>
                                </div>
                            </div>
                            {renderQuestionAnalytics(question)}
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SurveyAnalytics;
